import { Dictionary } from 'lodash';
import { Moment } from 'moment';
import {
  ResourceIdentifier,
  ResourceType,
} from 'api/resource-management/types';
import { AllocationEventModel } from 'components/organisms/resource-management/scheduler-config/models';
import {
  endDateParser,
  startDateParser,
} from 'components/organisms/resource-management/utils/dates';
import { isOverlapping } from 'components/organisms/resource-management/utils/events-conversion/common';
import { SchedulerEventType } from '../../event-types';

export const isEventBetweenDates = (
  { startDate, endDate }: AllocationEventModel,
  { fromDate, toDate }: { fromDate: Moment; toDate: Moment },
): boolean =>
  isOverlapping(
    {
      startDate: startDateParser(startDate),
      endDate: endDateParser(endDate),
    },
    {
      startDate: fromDate,
      endDate: toDate,
    },
  );

export const filterAllocationEvents = (
  events: AllocationEventModel[],
  {
    fromDate,
    toDate,
    resourceIdentifier,
    projectId,
  }: {
    fromDate?: Moment;
    toDate?: Moment;
    resourceIdentifier?: ResourceIdentifier;
    projectId?: number;
  },
): AllocationEventModel[] => {
  const uniqueEventsMap = events.reduce(
    (result, event) => ({
      ...result,
      [event.id]: event,
    }),
    {} as Dictionary<AllocationEventModel>,
  );

  return Object.values(uniqueEventsMap).filter((event) => {
    if (event.get('type') !== SchedulerEventType.Allocation) {
      return false;
    }
    if (resourceIdentifier != null) {
      if (resourceIdentifier.type === ResourceType.User) {
        if (event.get('userId') !== resourceIdentifier.userId) {
          return false;
        }

        /**
         * In case we need to filter events based on referenceTeamId
         * then need to add another optional boolean argument to this function
         * like `includesReferenceTeam` and filter based on the flag
         */
      }
      if (
        resourceIdentifier.type === ResourceType.Team &&
        event.get('teamId') !== resourceIdentifier.teamId
      ) {
        return false;
      }
      if (
        resourceIdentifier.type === ResourceType.Role &&
        !(
          event.get('roleId') === resourceIdentifier.roleId &&
          (resourceIdentifier.teamId ? event.get('teamId') != null : true)
        )
      ) {
        return false;
      }
    }
    if (projectId != null && event.get('projectId') !== projectId) {
      return false;
    }
    if (fromDate == null || toDate == null) {
      return true;
    }

    return isEventBetweenDates(event, {
      fromDate,
      toDate,
    });
  });
};

// TODO: These are not used as of now; To check and remove
// export const getMinimumStartDateFromEvents = (
//   allocations: AllocationEventModel[],
// ): Moment =>
//   allocations.reduce(
//     (minDate: Moment, { startDate }) =>
//       moment(startDate).isBefore(minDate, 'day')
//         ? moment(startDate).startOf('day')
//         : minDate,
//     maximumDate,
//   );
// export const getMaximumEndDateFromEvents = (
//   allocations: AllocationEventModel[],
// ): Moment =>
//   allocations.reduce(
//     (maxDate: Moment, { endDate }) =>
//       moment(endDate).isAfter(maxDate, 'day')
//         ? moment(endDate).endOf('day')
//         : maxDate,
//     minimumDate,
//   );
